/**
 * 存檔檔名的共用清理與組裝(REQ-20260903006)。
 *
 * 兩個來源共用這裡:
 *  - 單據頁的 `document.title`(瀏覽器另存 PDF 的預設檔名就是分頁標題,
 *    REQ-20260811002 定下的做法)
 *  - 「另存 JPEG」實際寫下去的檔名(`export-jpeg.ts`)
 *
 * 清理規則原本只長在 `features/ar-ap-reports/report-filename.ts` 裡,
 * 本次抽出來共用 —— 兩份規則各自演化的話,同一個客戶簡稱在報表與訂單上
 * 會被清成不一樣的字。`buildReportFilename()` 的輸出字串一字未變。
 */

/** 檔名不允許的字元 + 控制字元(一律用跳脫序列寫,不要把控制字元寫進原始碼) */
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS = /[/\\:*?"<>|\x00-\x1f\x7f]/g;

/** 存檔對話框塞得下就好,過長的檔名在某些檔案系統會被截斷 */
const MAX_LENGTH = 120;

/**
 * 把任意字串清成可以當檔名的樣子。
 *
 * `fallback` 是全部清光時的退路 —— 報表沿用它原本的「報表」,
 * 其餘單據用通用的「未命名」,免得存出一個空檔名。
 */
export function sanitizeFilename(name: string, fallback = '未命名'): string {
  const cleaned = name
    .replace(UNSAFE_CHARS, '-')
    .replace(/-+/g, '-')
    .replace(/^[-\s]+|[-\s]+$/g, '')
    .slice(0, MAX_LENGTH);
  return cleaned.length > 0 ? cleaned : fallback;
}

/**
 * 單據類的預設檔名:`表單類別-對象簡稱-單號`(REQ-20260903006 需求原文的格式)。
 *
 * 缺值的段落**直接省略**,不要補一個「無」或「全部」上去 ——
 * 沿用 `report-filename.ts` 既有的慣例:檔名裡沒有的東西就是沒有,不編假字。
 *
 * **不加副檔名**:另存 PDF 由瀏覽器自己補 `.pdf`(自己加會變成 `xxx.pdf.pdf`),
 * 另存 JPEG 則由 `exportNodeToJpeg()` 補 `.jpg`。
 */
export function buildDocumentFilename(parts: Array<string | null | undefined>): string {
  const segments = parts.filter((p): p is string => typeof p === 'string' && p.trim().length > 0);
  return sanitizeFilename(segments.join('-'));
}
